<template>
  <!-- Footer table -->
  <div v-if="maxPaginationNumber"
       class="customize-footer"
  >
    <div class="customize-footer__index">
      <select-per-page v-model="perPageValue"
                       :title="perPageTitle"
                       :options="perPageOptions"
      />
      <span v-if="total > 0"
            class="page-info"
      >
        Показаны <span class="page-info__digits">{{ fmt(currentPageFirstIndex) }}</span>–<span
          class="page-info__digits"
        >{{ fmt(currentPageLastIndex) }}</span> из <span class="page-info__digits">{{
          fmt(total) }}</span> {{ unitName }}
      </span>
    </div>
    <div class="pagination-row">
      <button class="prev-page"
              :disabled="isFirstPage"
              @click="prevPage"
      >
        <v-icon name="prev-page-icon"
                stroke="none"
                width="20"
                height="20"
                pointer
        />
      </button>

      <div class="pagination-row__buttons">
        <span v-for="item in paginationItems"
              :key="`p-${item}-${page}`"
              :class="[
                'pagination-row__button',
                { 'pagination-row__button-active': item === page },
                { 'pagination-row__button-dots': item === '…' },
              ]"
              @click="item === '…' ? null : updatePage(item)"
        >
          {{ item }}
        </span>
      </div>

      <button class="next-page"
              :disabled="isLastPage"
              @click="nextPage"
      >
        <v-icon name="next-page-icon"
                stroke="none"
                width="20"
                height="20"
                pointer
        />
      </button>
    </div>
  </div>
  <!-- End Footer -->
</template>

<script setup>
import { watch, computed } from 'vue';
import i18n from '@/i18n';
import SelectPerPage from '@/components/UI/table/SelectPerPage.vue';


const emit = defineEmits({
  'update:modelValue': null,
});

const props = defineProps({
  dataTable: {
    type: Object,
    default: () => ({}),
  },
  modelValue: {
    type: Number,
    default: 5,
  },
  total: {
    type: Number,
    default: 0,
  },
  serverOptions: {
    type: Object,
    default: () => ({ page: 1 }),
  },
  unitName: {
    type: String,
    default: '',
  },
  perPageTitle: {
    type: String,
    default: 'Отображать результатов:',
  },
  perPageOptions: {
    type: Array,
    default: () => [25, 50, 100, 150],
  },
});
const perPageValue = computed({
  get: () => props.modelValue,
  set: (newValue) => emit('update:modelValue', newValue),
});

const currentPaginationNumber = computed(() => props.dataTable?.currentPaginationNumber);
// pagination related
const maxPaginationNumber = computed(() => props.dataTable?.maxPaginationNumber);
const page = computed(() => Number(props?.serverOptions?.page || currentPaginationNumber.value || 1));
const currentPageFirstIndex = computed(() => {
  const rpp = Number(perPageValue.value || 0);
  const total = Number(props.total || 0);
  if (!total || !rpp) return 0;
  return (page.value - 1) * rpp + 1;
});
const currentPageLastIndex = computed(() => {
  const rpp = Number(perPageValue.value || 0);
  const total = Number(props.total || 0);
  if (!total || !rpp) return 0;
  return Math.min(page.value * rpp, total);
});
const nf = new Intl.NumberFormat(i18n.global.locale);
const fmt = (v) => nf.format(Number(v || 0));

const paginationItems = computed(() => {
  const total = Number(maxPaginationNumber.value || 0);
  const current = Number(page.value || 1);
  if (!total || total <= 0) return [];
  if (total <= 9) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const items = [];
  const push = (x) => {
    const last = items[items.length - 1];
    if (x === '…' && last === '…') return;
    if (x === last) return;
    items.push(x);
  };

  push(1);
  const left = Math.max(2, current - 1);
  const right = Math.min(total - 1, current + 1);

  if (left > 2) push('…');
  for (let i = left; i <= right; i++) push(i);
  if (right < total - 1) push('…');

  push(total);
  return items;
});

const isFirstPage = computed(() => Number(page.value || 1) <= 1);
const isLastPage = computed(() => {
  const max = Number(maxPaginationNumber.value || 0);
  if (!max) return true;
  return Number(page.value || 1) >= max;
});

const nextPage = () => {
  props.dataTable.nextPage();
};
const prevPage = () => {
  props.dataTable.prevPage();
};
const updatePage = (paginationNumber) => {
  props.dataTable.updatePage(paginationNumber);
};
const updateRowsPerPageSelect = () => {
  props.dataTable.updateRowsPerPageActiveOption(Number(perPageValue.value));
};
watch(perPageValue, () => {
  updateRowsPerPageSelect();
});
/* END Footer Table */
</script>

<style lang="scss" scoped>
.customize-footer {
  background: var(--color-white);
  height: 66px;
  position: sticky;
  bottom: -16px;
  top: -32px;
  width: 100%;
  padding: 0px 12px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  align-content: flex-end;
  z-index: 5;
  box-shadow: 0 -1px 1px rgba(0, 0, 0, 0.01);
  padding: 8px 0;

  &__index {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 16px;
  }
}

.pagination-row {
  display: flex;
  height: 100%;
  align-items: center;

  &__buttons {
    display: flex;
  }

  &__button {
    border-radius: 8px;
    padding: 4px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    min-width: 28px;
    height: 28px;
    position: relative;
    text-align: center;
    font: 400 12px/130% 'Inter', sans-serif;
    color: var(--color-gray-dark);
    cursor: pointer;
  }

  &__button:not(&__button-active):hover {
    background: var(--color-gray-light);
  }

  &__button-active {
    background: var(--color-accent);
    color: var(--color-white);
    cursor: auto;
  }

  &__button-dots {
    cursor: default;
    pointer-events: none;
    color: #9b9b9b;
  }
}

.page-info {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  margin-right: 12px;
  color: var(--color-gray-dark);
  font: 400 12px/130% 'Inter', sans-serif;
}

.page-info__digits {
  color: var(--color-gray-dark);
  text-align: left;
  font: 500 12px/130% "Inter", sans-serif;
}

.prev-page,
.next-page {
  border: 0;
  background-color: transparent;
  outline: none;
  display: flex;
  width: 28px;
  height: 28px;
  padding: 8px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
}

.page-info {
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 130%;
  color: #9b9b9b;
}
</style>
